import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { Badge } from './ui/badge';
import { Flame, Droplet, Snowflake, Calendar as CalendarIcon } from 'lucide-react';
import { toast } from 'sonner';
import { formatDate } from '../utils/helpers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Lost leads live on the dedicated /lost-leads page — Kanban only shows the active pipeline.
const COLUMNS = [
  { key: 'New',           title: 'New',           tint: 'bg-slate-50 border-slate-200' },
  { key: 'Contacted',     title: 'Contacted',     tint: 'bg-blue-50 border-blue-200' },
  { key: 'Interested',    title: 'Interested',    tint: 'bg-indigo-50 border-indigo-200' },
  { key: 'Proposal Sent', title: 'Proposal Sent', tint: 'bg-amber-50 border-amber-200' },
  { key: 'Converted',     title: 'Converted',     tint: 'bg-emerald-50 border-emerald-200' },
];

const TempIcon = ({ t }) => {
  if (t === 'Hot') return <Flame className="h-3.5 w-3.5 text-orange-500" />;
  if (t === 'Cold') return <Snowflake className="h-3.5 w-3.5 text-blue-400" />;
  return <Droplet className="h-3.5 w-3.5 text-blue-600" />;
};

const LeadKanban = ({ leads, onLeadUpdated }) => {
  const [dragId, setDragId] = useState(null);
  const [overCol, setOverCol] = useState(null);

  const grouped = useMemo(() => {
    const map = Object.fromEntries(COLUMNS.map(c => [c.key, []]));
    for (const l of leads) {
      const k = COLUMNS.find(c => c.key === l.status) ? l.status : 'New';
      map[k].push(l);
    }
    return map;
  }, [leads]);

  const handleDrop = async (targetStatus) => {
    setOverCol(null);
    if (!dragId) return;
    const lead = leads.find(l => l.lead_id === dragId);
    setDragId(null);
    if (!lead || lead.status === targetStatus) return;
    try {
      await axios.put(`${API}/leads/${lead.lead_id}`, { status: targetStatus }, { withCredentials: true });
      toast.success(`Moved to ${targetStatus}`);
      if (onLeadUpdated) onLeadUpdated();
    } catch (e) {
      console.error('Kanban move failed:', e);
      toast.error(e.response?.data?.detail || 'Failed to update status');
    }
  };

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="lead-kanban">
      {COLUMNS.map(({ key, title, tint }) => (
        <div
          key={key}
          onDragOver={(e) => { e.preventDefault(); setOverCol(key); }}
          onDragLeave={() => setOverCol((c) => (c === key ? null : c))}
          onDrop={() => handleDrop(key)}
          className={`rounded-lg border ${tint} min-h-[220px] flex flex-col ${overCol === key ? 'ring-2 ring-blue-400' : ''}`}
          data-testid={`kanban-col-${key.toLowerCase().replace(/\s+/g, '-')}`}
        >
          <div className="px-3 py-2 border-b border-black/5 flex items-center justify-between">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-700">{title}</p>
            <span className="text-[10px] bg-white/70 text-slate-600 px-1.5 py-0.5 rounded">{grouped[key].length}</span>
          </div>
          <div className="p-2 space-y-2 flex-1">
            {grouped[key].length === 0 && (
              <p className="text-[11px] text-slate-400 text-center py-4">Drop leads here</p>
            )}
            {grouped[key].map((l) => (
              <article
                key={l.lead_id}
                draggable
                onDragStart={() => setDragId(l.lead_id)}
                onDragEnd={() => setDragId(null)}
                className="bg-white rounded-md border border-slate-200 p-2 cursor-grab active:cursor-grabbing hover:shadow-md hover:border-blue-300 transition-all"
                data-testid={`kanban-card-${l.lead_id}`}
              >
                <p className="text-sm font-semibold text-slate-900 truncate">{l.name}</p>
                {l.product && (
                  <p className="text-[11px] text-slate-600 truncate">{l.product}</p>
                )}
                <div className="flex items-center justify-between mt-1.5">
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 flex items-center gap-1">
                    <TempIcon t={l.temperature} />
                    {l.temperature}
                  </Badge>
                  {l.follow_up_date && (
                    <span className="flex items-center gap-0.5 text-[10px] text-slate-500">
                      <CalendarIcon className="h-3 w-3" />
                      {formatDate(l.follow_up_date)}
                    </span>
                  )}
                </div>
              </article>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default LeadKanban;
